// Idle latency, jitter, download and download-loaded latency via @cloudflare/speedtest against our own Worker.
// Same download sequence and settings as the Phase 0 spike; the engine's own upload phase is not used (it under-reads,
// research §15), Method A measures upload instead.
import SpeedTest, { type ConfigOptions, type MeasurementConfig, type Results } from '@cloudflare/speedtest';
import { DOWNLOAD_PATH, UPLOAD_PATH } from '../../shared/limits';
import { MeasurementError, type EngineRaw, type TestStage } from './types';

// Each bandwidth type stops early once every request in a round takes longer than bandwidthFinishRequestDuration,
// so slower links end on smaller payloads.
export const ENGINE_MEASUREMENTS: MeasurementConfig[] = [
  { type: 'latency', numPackets: 1 },
  { type: 'download', bytes: 1e5, count: 1, bypassMinDuration: true },
  { type: 'latency', numPackets: 20 },
  { type: 'download', bytes: 1e5, count: 3 },
  { type: 'download', bytes: 1e6, count: 3 },
  { type: 'download', bytes: 4e6, count: 2 },
  { type: 'download', bytes: 1e7, count: 2 },
  { type: 'download', bytes: 25e6, count: 2 },
  { type: 'download', bytes: 5e7, count: 2 },
  { type: 'download', bytes: 1e8, count: 2 },
];

/** The first two steps only warm up the connection. */
const WARM_UP_STEPS = 2;

export const ENGINE_CONFIG = {
  autoStart: false,
  downloadApiUrl: DOWNLOAD_PATH,
  uploadApiUrl: UPLOAD_PATH,
  // null disables the engine's result reporting to speed.cloudflare.com and its per-request logging.
  logAimApiUrl: null,
  logMeasurementApiUrl: null,
  measurements: ENGINE_MEASUREMENTS,
  measureDownloadLoadedLatency: true,
  measureUploadLoadedLatency: false,
  bandwidthFinishRequestDuration: 1000,
  loadedLatencyThrottle: 400,
  loadedRequestMinDuration: 250,
  latencyPercentile: 0.5,
  bandwidthPercentile: 0.9,
} satisfies ConfigOptions;

const orNull = (value: number | null | undefined): number | null => value ?? null;
// The engine's percentile() returns 0 for an empty sample set, so "no samples" must not be read as 0 ms / 0 Mbps.
const ifSampled = (samples: unknown[], value: number | null | undefined): number | null =>
  samples.length ? orNull(value) : null;

export function readEngineResults(results: Results): Omit<EngineRaw, 'durationMs' | 'protocols'> {
  const idle = results.getUnloadedLatencyPoints();
  const loaded = results.getDownLoadedLatencyPoints();
  const download = results.getDownloadBandwidthPoints();
  const bps = results.getDownloadBandwidth();
  return {
    downloadMbps: bps ? bps / 1e6 : null,
    idleLatencyMs: ifSampled(idle, results.getUnloadedLatency()),
    idleJitterMs: idle.length > 1 ? orNull(results.getUnloadedJitter()) : null,
    loadedLatencyMs: ifSampled(loaded, results.getDownLoadedLatency()),
    loadedJitterMs: loaded.length > 1 ? orNull(results.getDownLoadedJitter()) : null,
    idleLatencySamples: idle.length,
    loadedLatencySamples: loaded.length,
    downloadBytes: download.reduce((sum, point) => sum + point.bytes, 0),
  };
}

export function stageForStep(step: number, measurement: MeasurementConfig): TestStage {
  if (step < WARM_UP_STEPS) return 'connecting';
  return measurement.type === 'latency' ? 'latency' : 'download';
}

export function runEngine(options: { onStage?: (stage: TestStage) => void; signal?: AbortSignal } = {}): Promise<EngineRaw> {
  const { onStage, signal } = options;
  return new Promise<EngineRaw>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const t0 = performance.now();
    const engine = new SpeedTest(ENGINE_CONFIG);
    const protocols = new Set<string>();
    const record = (entries: PerformanceEntryList) => {
      for (const entry of entries) {
        const timing = entry as PerformanceResourceTiming;
        const url = new URL(timing.name);
        if (url.origin === location.origin && url.pathname === DOWNLOAD_PATH) {
          protocols.add(timing.nextHopProtocol || 'not reported');
        }
      }
    };
    // The engine clears the resource-timing buffer between rounds, so entries are captured as they arrive.
    const observer = new PerformanceObserver((list) => record(list.getEntries()));
    observer.observe({ type: 'resource' });

    let settled = false;
    const settle = () => {
      settled = true;
      record(observer.takeRecords());
      observer.disconnect();
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      if (settled) return;
      engine.pause();
      settle();
      reject(signal?.reason);
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    engine.onPhaseChange = ({ measurementId, measurement }) => {
      if (!settled) onStage?.(stageForStep(measurementId, measurement));
    };
    engine.onFinish = (results) => {
      if (settled) return;
      settle();
      resolve({ ...readEngineResults(results), durationMs: performance.now() - t0, protocols: [...protocols] });
    };
    engine.onError = (message, status) => {
      if (settled) return;
      engine.pause();
      settle();
      const code = status && status >= 400 ? 'server' : 'network';
      reject(new MeasurementError(code, message, undefined, { status: status ?? null, partial: readEngineResults(engine.results) }));
    };
    engine.play();
  });
}
